import { toast } from "react-toastify";
import qs from "qs";
import API from "../../Api/Api";
import NEW_API from "../../Api/NewApi";
import { API_END_POINT } from "../../Api/EndPoints";
import { uploadFile, isDefined } from "../../Utils";
import { scopeParams } from "./appCustomers";

// ---------------------------------------------------------------------------
// Competitors (competitor stores) — /api/competitors, per-organization
// scoping enforced server-side. Rows keep the Strapi v4 envelope shape
// ({ id, attributes }) like the prospect table.
// ---------------------------------------------------------------------------

const LIST_POPULATE = {
  activities: { fields: ["id", "name", "name_en"] },
  spaMarques: { fields: ["id", "name"] },
  organization: { fields: ["id", "name"] },
};

// filters: the table's { competitorType, poolSpaGroup, spaMarque } options.
export const fetchCompetitors =
  (search, page = 1, signal, filters = {}) =>
  async (dispatch, getState) => {
    try {
      if (page === 1) dispatch({ type: "FETCH_COMPETITORS_LOADING" });
      const and = [];
      if (search) and.push({ companyName: { $containsi: search } });
      if (filters?.competitorType?.code) {
        and.push({ competitorType: { $eq: filters.competitorType.code } });
      }
      if (filters?.poolSpaGroup?.code) {
        and.push({ poolSpaGroup: { $eq: filters.poolSpaGroup.code } });
      }
      if (filters?.spaMarque?.code) {
        and.push({ spaMarques: { id: { $eq: filters.spaMarque.code } } });
      }
      const query = qs.stringify({
        populate: LIST_POPULATE,
        sort: ["createdAt:desc"],
        pagination: { page, pageSize: 25 },
        ...(and.length ? { filters: { $and: and } } : {}),
        ...scopeParams(getState),
      });
      const { data } = await NEW_API.get(`api/competitors?${query}`, {
        signal,
      });
      dispatch({
        type:
          page === 1
            ? "FETCH_COMPETITORS_SUCCESS"
            : "FETCH_COMPETITORS_LOAD_MORE_SUCCESS",
        payload: data,
      });
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
        return;
      console.log(error);
      dispatch({ type: "FETCH_COMPETITORS_ERROR", payload: error });
    }
  };

// Full rows with a marker, for the Carte tab (popup "Voir plus" opens the
// edit modal, so the map rows need the same populate as the list).
export const fetchCompetitorsMap = (signal) => async (dispatch, getState) => {
  try {
    dispatch({ type: "FETCH_COMPETITORS_MAP_LOADING" });
    const query = qs.stringify({
      populate: LIST_POPULATE,
      filters: { markers: { $notNull: true } },
      pagination: { pageSize: 100000 },
      ...scopeParams(getState),
    });
    const { data } = await NEW_API.get(`api/competitors?${query}`, { signal });
    dispatch({
      type: "FETCH_COMPETITORS_MAP_SUCCESS",
      payload: data?.data ?? [],
    });
  } catch (error) {
    if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
      return;
    console.log(error);
    dispatch({ type: "FETCH_COMPETITORS_MAP_ERROR" });
  }
};

export const createUpdateCompetitor =
  (formData, id, t, callback) => async (dispatch) => {
    dispatch({ type: "CREATE_UPDATE_COMPETITOR_LOADING" });
    try {
      // Selects carry { name, code } — unwrap to the stored code.
      const data = { ...formData };
      for (const field of [
        "storeArea",
        "poolSpaGroup",
        "traffic",
        "digitalAds",
        "competitorType",
      ]) {
        data[field] = formData[field]?.code ?? null;
      }
      // Store picked by an admin / business affiliate (a reseller's is set
      // server-side); absent = unchanged.
      if (formData.organization !== undefined) {
        data.organization = formData.organization?.code ?? null;
      }

      const query = qs.stringify({ populate: LIST_POPULATE });
      const { data: response } = await NEW_API[isDefined(id) ? "put" : "post"](
        `api/competitors/${id ?? ""}?${query}`,
        { data }
      );
      dispatch({
        type: isDefined(id)
          ? "UPDATE_COMPETITOR_SUCCESS"
          : "CREATE_COMPETITOR_SUCCESS",
        payload: response?.data,
      });
      toast.success(
        isDefined(id)
          ? t("Competitor successfully updated!")
          : t("Competitor successfully created!")
      );
      if (callback) callback(response?.data);
    } catch (error) {
      console.log(error);
      dispatch({ type: "CREATE_UPDATE_COMPETITOR_ERROR" });
      toast.error(t("An error has occurred!"));
    }
  };

export const deleteCompetitor = (id, t) => async (dispatch) => {
  try {
    dispatch({ type: "DELETE_COMPETITOR_LOADING" });
    await NEW_API.delete(`api/competitors/${id}`);
    dispatch({ type: "DELETE_COMPETITOR_SUCCESS", payload: id });
    toast.success(t("Competitor successfully deleted!"));
  } catch (error) {
    console.log(error);
    dispatch({ type: "DELETE_COMPETITOR_ERROR" });
    toast.error(t("An error has occurred!"));
  }
};

// COMMENTS — same machinery as prospect comments, targeting `competitor`.
export const getCompetitorComments =
  (competitor, signal) => async (dispatch) => {
    try {
      dispatch({ type: "FETCH_COMMENTS_COMPETITOR_LOADING" });
      const query = qs.stringify({
        populate: [
          "attachments.file",
          "user.photo",
          "user.marque",
          "emojis.user",
        ],
        sort: ["createdAt:desc"],
        filters: {
          competitor: { id: { $eq: competitor } },
        },
      });
      const {
        data: { data: commentData },
      } = await API.get(`${API_END_POINT}api/comments?${query}`, { signal });
      dispatch({
        type: "FETCH_COMMENTS_COMPETITOR_SUCCESS",
        payload: commentData,
      });
    } catch (error) {
      if (error.name === "CanceledError") return;
      dispatch({ type: "FETCH_COMMENTS_COMPETITOR_ERROR", payload: error });
    }
  };

export const createCompetitorComment =
  (comment, competitor, user, successCallback, t) => async (dispatch) => {
    dispatch({ type: "CREATE_COMMENTS_COMPETITOR_LOADING" });
    try {
      const {
        data: { data: commentData },
      } = await API.post(`${API_END_POINT}api/comments?populate=*`, {
        data: {
          text: comment?.text,
          competitor,
          user,
        },
      });

      if (comment?.attachments?.length > 0) {
        for (const file of comment?.attachments) {
          const fileId = await uploadFile(file);
          const { data } = await API.post(
            `${API_END_POINT}api/attachments?populate=*`,
            {
              data: {
                date: new Date(),
                file: fileId,
                comment: commentData?.id,
              },
            }
          );
          commentData.attributes.attachments.data?.push(data?.data);
        }
      }

      dispatch({
        type: "CREATE_COMMENTS_COMPETITOR_SUCCESS",
        payload: { ...commentData, attributes: { ...commentData?.attributes } },
      });
      successCallback();
      toast.success(t("A comment has been added"));
    } catch (error) {
      console.log(error);
    }
  };
